import gql from 'graphql-tag'
import moment from 'moment'
import duplicator from '@/mixins/pagination/providers/duplicator'
import resolver from '@/assets/modules/error/resolver'
import cvDummy from '@/mixins/pagination/providers/dummyData/campaignViewer'
import acceptedTypes from '@/assets/configurations/mpe/acceptedTypes'

export default {
  data () {
    return {
      mediaPlanEvents: {},
      campaigns: {},
      advertisables: {},
      eventsInited: false
    }
  },

  methods: {
    /**
     * Filters MPE's. Date filtering happens on API
     * @returns {Array} Array of filtered MPE's
     */
    filterEvents (events) {
      let base = events
      if (!this.getFilterFields) {
        return base
      }

      // getFilterFields
      if (this.getFilterFields.type !== null) {
        base = base.filter(mpe => mpe.advertisable.type === this.getFilterFields.type)
      }

      if (this.getFilterFields.channel !== null) {
        base = base.filter(mpe => (mpe.credentials || {}).id === this.getFilterFields.channel)
      }

      return base
    },

    /**
     * Tells the status of the given MPE
     * @param {String} id MPE's id
     */
    getMpeStatus (id) {
      let interestedMpe = this.getMediaEvents.filter(ece => ece.id === id)[0] || {}
      return interestedMpe.status
    },

    /**
     * Gives initial date range for given dates & style
     * @param {String} start
     * @param {String} end
     * @param {String} style
     */
    getInitialDateRange (start, end, style) {
      let from, to, id
      let stepSize = resolver.getDateStepSize(style)

      if (!resolver.isCalendarView(style)) {
        from = moment(start).startOf(stepSize).toISOString()
        to = moment(end).endOf(stepSize).toISOString()
        id = from
      } else {
        from = moment(start).startOf(stepSize).toISOString()
        to = moment(start).endOf(stepSize).toISOString()
        id = from
      }

      return {
        from,
        to,
        id
      }
    }
  },

  computed: {

    /**
     * Gells if tour is running
     * @returns {Boolean}
     */
    isTourRunning () {
      return ((this.$tours || {}).mainPageTour || {}).isRunning
    },

    /**
     * Gives data of current edited campaign
     * @returns {Object} Edited campaign's object
     */
    getEditedCampaignData () {
      let campaignSet = ((this.isEditingCampaign) ? this.campaigns.set : this.advertisables.set) || []
      if(this.isTourRunning && !campaignSet.length) return {}

      /**
       * If showing advertisable, use it's events to determine start/end dates
       */
      if (!this.isEditingCampaign && !this.eventsInited) {
        let mpes = ((campaignSet[0] || {}).events || {}).set || []
        if (mpes.length) {
          this.eventsInited = true

          let startsAt
          let expiresAt

          for (let mpe of mpes) {
            let { publishAt } = mpe
            publishAt = moment(publishAt)

            if (!startsAt || publishAt.isSameOrBefore(startsAt)) {
              startsAt = publishAt
            }
            if (!expiresAt || publishAt.isSameOrAfter(expiresAt)) {
              expiresAt = publishAt
            }
          }
          startsAt = (startsAt || moment()).toISOString()
          expiresAt = (expiresAt || moment()).toISOString()

          return { ...campaignSet[0], ...{ startsAt, expiresAt } }
        }
      }
      return campaignSet[0] || {}
    },

    /**
     * Gives edited campaign's meta field
     * @returns {Object}
     */
    getEditedCampaignMeta () {
      return this.getEditedCampaignData.meta || {}
    },

    /**
     * Gives credentials used in this campaign
     * @returns {Array}
     */
    getSelectedChannels () {
      let rtr = {}
      let campaign = this.campaigns.set || []
      if (campaign.length <= 0) return []

      campaign = campaign[0]

      let { events: { set: eventSet } } = campaign
      for (let e of eventSet || []) {
        let { id, type } = e.credentials
        rtr[id] = {
          credentials: id,
          mutations: undefined,
          type: type
        }
      }

      return Object.values(rtr)
    },

    /**
     * Tells edited campaign's start date
     * @returns {String}
     */
    getEditedCampaignStartDate () {
      if (this.isEditingCampaign)
        return moment(this.getEditedCampaignData.startsAt).startOf('day').toISOString()

      return null
    },

    /**
     * Tells edited campaign's end date
     * @returns {String}
     */
    getEditedCampaignEndDate () {
      if (this.isEditingCampaign)
        return moment(this.getEditedCampaignData.expiresAt).endOf('day').toISOString()

      return null
    },

    /**
     * Returnes edited campaign's type
     * @returns {String} Edited campaign's type
     */
    getEditedCampaignType () {
      return this.getEditedCampaignData.type
    },

    /**
     * Takes received mpe's and strips unneeded data, prepares them for display
     * @returns {Array} Array of stripped mpe objects
     */
    getMediaEvents () {
      // If editing advertisable, filter out mpes that belong to a campaign; since campaign can reuse mpe's advertisable.
      let eventsSet = (this.mediaPlanEvents.set || []).filter(({ campaign }) => this.isEditingCampaign || !campaign)
      if(this.isTourRunning && !eventsSet.length) return cvDummy.get()
      return eventsSet
    }
  },

  watch: {
    getEditedCampaignData: {
      handler: function(newValue, oldValue) {
        // Set default date filter
        if (Object.keys(newValue).length) {
          let { startsAt, expiresAt } = newValue
          let filter = this.getInitialDateRange(startsAt, expiresAt, this.style)
          if (this.isEditingCampaign) {
            this.setFilter('date', { ...filter, ...this.getViewedDate})
          } else {
            this.setFilter('date', filter)
          }
        }
      },

      deep: true
    }
  },

  mixins: [
    duplicator
  ],

  // Apollo...
  apollo: {
    /**
     * Media plan events for the given campaign...
     */
    mediaPlanEvents: {
      query: gql`query ($scheduledFrom: Time, $scheduledUntil: Time, $campaigns: [UUID!], $type: [MediaPlanEventType!], $advertisables: [UUID!]) {
        mediaPlanEvents (scheduledFrom: $scheduledFrom, scheduledUntil: $scheduledUntil, campaigns: $campaigns, type: $type, advertisables: $advertisables, limit: -1) {
          set {
            id,
            scheduledAt: publishAt,
            publishAt,
            removeAt,
            type,
            status,
            link,

            channel: credentials {
              id,
              label,
              type
            },

            credentials {
              id,
              label,
              type
            },

            campaign {
              id,
              type,
              meta {
                ... on CampaignSale {
                  discount
                },
                ... on CampaignDailyDeal {
                  discount
                }
              }
            }

            advertisable {
              id,
              label,
              url,

              type,

              meta {
                ... on AdvertisableImportedProduct {
                  description,
                  price
                  discount
                },
                ... on  AdvertisableProduct {
                  description,
                  price,
                  discount
                },
                ... on  AdvertisablePost {
                  description: content
                }
              }
      
              media {
                set {
                  url
                }
              }
            },
            payload {
              ... on MediaPlanEventFacebookPost {
                body: message,
                message,
                link,
                photos {
                  url
                }
              }
              ... on MediaPlanEventSqwizArticle {
                title,
                body,
                images {
                  url
                }
              }
              ... on  MediaPlanEventSqwizProduct{
                title,
                descriptionShort,
                price,
                discount: discountPercent,
                images {
                  url
                }
              },
              ... on MediaPlanEventTwitterTweet {
                status,
                statusCut,
                media {
                  url
                }
              },
              
              ... on MediaPlanEventInformacijaProduct {
                name,
                model,
                shortDescription,
                price,
                discount,
                images {
                  url
                }
              },
              
              ... on MediaPlanEventInformacijaPost {
                name,
                shortDescription,
                images {
                  url,
                  uuid: url
                }
              },

              ... on MediaPlanEventWordpressPost {
                title,
                content,
                media {
                  url,
                  uuid: url
                }
              },
              ... on MediaPlanEventWoocommerceProduct {
                name,
                shortDescription,
                description,
                regularPrice,
                salePrice,
                categories,
                images {
                  url,
                  uuid: url
                }
              }
            }
          }
        }
      }`,

      variables () {
        return {
          scheduledFrom: this.getScheduledFrom,
          scheduledUntil: this.getScheduledUntil,
          campaigns: this.getCampaigns,
          advertisables: this.getAdvertisables,
          type: acceptedTypes
        }
      },

      fetchPolicy: 'cache-and-network'
    },

    /**
     * Campaign information
     */
    campaigns: {
      query: gql`query ($id: [UUID!], $limit: Int!) {
        campaigns (id: $id, limit: $limit) {
          set {
            id
            label,
            type,
            startsAt,
            expiresAt,

            status,
            events {
              set {
                credentials {
                  id,
                  type
                },
                advertisable {
                  id
                }
              }
            },

            meta {
              ... on CampaignSale {
                discount
              },
              ... on CampaignDailyDeal {
                discount
              }
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.campaignId,
          limit: this.getLimit || 1
        }
      },
      fetchPolicy: 'cache-and-network'
    },

    /**
     * Advertisable information
     */
    advertisables: {
      query: gql`query ($id: [UUID!], $limit: Int!) {
        advertisables (id: $id, limit: $limit) {
          set {
            id
            label,
            type,
            events {
              set {
                id,
                publishAt
              }
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.campaignId,
          limit: this.getLimit || 1
        }
      },
      fetchPolicy: 'cache-and-network'
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/pagination/providers/campaignEditor.js